import { makeAutoObservable, runInAction } from "mobx";
import OpenAI from "openai";
import { MD5 } from "crypto-js";
import { CSSProperties } from "react";

import { assert } from "console";
import { Firestore, collection, onSnapshot } from "firebase/firestore";
import { allowedUnits, HeatmapWords, IExtractionValue, IHeatmapData, IHeatmapWord, TemplateCellTuple, TemplateMetaData } from "../../../../shared/common/Azure";
import { logDebug, logDebugC, logDebugOnce, logError, logWarning, logWarningNoStack } from "../../../../shared/common/logger";
import SharedUtils from "../../../../shared/common/SharedUtils";
import { LocalLargeStorage } from "../../../../utils/PrivateUtils";
import RestAPI from "../../../../utils/restAPI";
import { IDatabaseLineItem } from "../../LineItemDBCreationToolPage";
import { IDatabaseMapping } from "../../../../shared/common/EstimateEngine/EstimateEngineTypes";
import { selectablesMap } from "../../../../shared/common/EstimateEngine/TicSelectables";

export interface IPerCellRenderingData {
	leftClean: string[];
	rightClean: string[];
	selectables: string[];
	leftFull: string[];
	rightFull: string[];
}

export interface ICandidatesNoFilter {
	words: [
		{
			word: string;
			confidence: number;
		}[],
		{
			word: string;
			confidence: number;
		}[]
	];
	selectedWords: string[];
}
class MappingStore {
	public templateData: TemplateMetaData | undefined;
	public extractionData: { [s: string]: IExtractionValue[] } | undefined;
	private _loading: number = 0;
	private _forceFetchMetadata: boolean = false; //SharedUtils.showDebug;
	private _loadDataFromServerCalledForType = "";
	private _privateLargeStorage = new LocalLargeStorage("LassoMappingStore");

	constructor() {
		logDebug("MappingStore constructor");
		makeAutoObservable(this);
		// this._loadDataFromServer();
	}

	getCellTupleFromId(id: string): TemplateCellTuple | undefined {
		if (!this.templateData) return;
		for (const row of this.templateData.rows || []) {
			for (const cell of row.cellTuples) {
				if (cell.meta?.id === id) return cell;
			}
		}
	}

	getImageForCandidate(selectedTupple: TemplateCellTuple, ticInput: string): string[] {
		const ret: string[] = ["/assets/ui/x-close.svg"];
		for (const v of selectedTupple.meta?.heatmapData || []) {
			const words = this.getCandidates(v);
			if (words.find((w) => w.trim().toLowerCase() === ticInput.trim().toLowerCase())) {
				ret.push(`data:image/png;base64,${v.region64}`);
			}
		}

		return ret;
	}

	public async loadData(force: boolean, fetchHeatmap: boolean, ticType: string) {
		if (this._loadDataFromServerCalledForType === ticType && !force) return;
		this._loadDataFromServerCalledForType = ticType;
		const prev = this._forceFetchMetadata;
		this._forceFetchMetadata = force;
		await this._loadDataFromServer(fetchHeatmap, ticType);
		this._forceFetchMetadata = prev;
	}

	// TODO :: use EngineUtils function instead
	public replaceBadScanCharacters(inputWords: IHeatmapWord[]): IHeatmapWord[] {
		const ocrCorrectionMap: { [s: string]: string } = {
			Á: "A",
			À: "A",
			Â: "A",
			Ä: "A",
			Å: "A",
			Ã: "A",
			á: "a",
			à: "a",
			â: "a",
			ä: "a",
			å: "a",
			ã: "a",
			É: "E",
			È: "E",
			Ê: "E",
			Ë: "E",
			é: "e",
			è: "e",
			ê: "e",
			ë: "e",
			Í: "I",
			Ì: "I",
			Î: "I",
			Ï: "I",
			í: "i",
			ì: "i",
			î: "i",
			ï: "i",
			Ó: "O",
			Ò: "O",
			Ô: "O",
			Ö: "O",
			Õ: "O",
			ó: "o",
			ò: "o",
			ô: "o",
			ö: "o",
			õ: "o",
			Ú: "U",
			Ù: "U",
			Û: "U",
			Ü: "U",
			ú: "u",
			ù: "u",
			û: "u",
			ü: "u",
			Ç: "C",
			ç: "c",
			Ñ: "N",
			ñ: "n",
			Ý: "Y",
			ý: "y",
			ÿ: "y",
			Ø: "O",
			ø: "o",
			Æ: "AE",
			æ: "ae",
			ß: "ss",
			Œ: "OE",
			œ: "oe",
			"×": "x",
		};
		return inputWords.map((w) => ({
			word: w.word
				.split("")
				.map((char) => ocrCorrectionMap[char] || char)
				.join(""),
			confidence: w.confidence,
		}));
	}

	public getCandidatesWithoutFilter(_v: IHeatmapData): ICandidatesNoFilter {
		const v = SharedUtils.clone(_v) as IHeatmapData;
		const template = [v.template[0].toLowerCase().replace(/\s/g, " ").split(" "), v.template[1].toLowerCase().replace(/\s/g, " ").split(" ")];
		v.words[0] = this.replaceBadScanCharacters(v.words[0]);
		v.words[1] = this.replaceBadScanCharacters(v.words[1]);

		v.words[0] = v.words[0].filter((w) => !template[0].includes(w.word.toLowerCase()));
		// TODO :: this is a temporary fix. We need to take better care of the selected words
		v.words[0].push(
			...v.selectedWords.map((w) => ({
				word: w.replace("(", "").replace(")", "").trim(),
				confidence: 1,
			}))
		);

		v.words[1] = v.words[1].filter((w) => !template[1].includes(w.word.toLowerCase()));
		return { words: v.words, selectedWords: v.selectedWords };
	}

	public getCandidates(_v: IHeatmapData) {
		const v = SharedUtils.clone(_v) as IHeatmapData;
		const template = [v.template[0].toLowerCase().replace(/\s/g, " ").split(" "), v.template[1].toLowerCase().replace(/\s/g, " ").split(" ")];
		v.words[0] = v.words[0].filter((w) => !template[0].includes(w.word.toLowerCase()));
		v.words[1] = v.words[1].filter((w) => !template[1].includes(w.word.toLowerCase()));

		const confidence0 = Math.min(...v.words[0].map((w) => w.confidence));
		const confidence1 = Math.min(...v.words[1].map((w) => w.confidence));
		if (confidence0 > 0.9 && confidence1 > 0.9) {
			const words = [...v.words[0].map((v) => v.word), ...v.words[1].map((v) => v.word)];
			const filteredWords = words.map((v) => {
				if (!isNaN(Number(v))) return "";
				v = v.trim();

				v = v.replaceAll("(", "");
				v = v.replaceAll(")", "");

				while (v.startsWith(",") || v.startsWith(".")) {
					v = v.substring(1);
				}
				while (v.endsWith(",") || v.endsWith(".")) {
					v = v.substring(0, v.length - 1);
				}

				let tmp = allowedUnits.find((u) => v.toLowerCase().endsWith(u));
				if (tmp) {
					v = v.substring(0, v.length - tmp.length);
				}

				tmp = allowedUnits.find((u) => v.toLowerCase().startsWith(u));
				if (tmp) {
					v = v.substring(tmp.length);
				}

				if (allowedUnits.includes(v.toLowerCase())) {
					return "";
				}

				if (!isNaN(Number(v))) return "";

				// if v contains a number replace it with {N}
				v = v.replace(/\d+/g, "{N}");

				return v;
			});
			const cArr = SharedUtils.removeDuplicateAndEmpty(filteredWords);
			return cArr;
		}
		return [];
	}

	private _extractCandidates(heatmapData: IHeatmapData[]) {
		let candidates = SharedUtils.removeDuplicateAndEmpty(
			heatmapData
				.map((v, ind) => {
					return this.getCandidates(v);
				})
				.flatMap((v) => v)
		);

		// remove numbers
		// candidates = candidates.filter((v) => isNaN(Number(v)));
		return candidates;
	}

	private _setIdToCell(
		cell: TemplateCellTuple,
		cellPos: {
			rowIndex: number;
			cellIndex: number;
		},
		templateData: TemplateMetaData,
		heatmapData: { [s: string]: IHeatmapData[] },
		maxHeatmapValue: number,
		ticType: string
	) {
		if (cell.meta?.isCategory) return;

		for (let i = cellPos.rowIndex || 0; i >= 0; i--) {
			if (templateData.rows![i].cellTuples[cellPos.cellIndex].meta?.isCategory) {
				// TODO :: move all id and selectables initialization to the server
				let cat = templateData.rows![i].cellTuples[cellPos.cellIndex];
				cell.meta = cell.meta || {};
				cell.meta.heatMapProcessedData = cell.meta.heatMapProcessedData || {};
				let cellContent = cell.cells[0].content.trim();
				cell.meta.id = `${cat.cells[0].content} ${cellContent}`;
				cell.meta.id = cell.meta.id.replace("'", "");
				if (ticType === "exterior") {
					cell.meta.id = cell.meta.id.replace("=", "");
				}
				cell.meta.id = cell.meta.id.replace("&", "and");
				cell.meta.id = cell.meta.id.replaceAll("(", "").replaceAll(")", "");
				cell.meta.id = cell.meta.id.trim().replace(/\s/g, "_").toLowerCase();
				cell.meta.id = cell.meta.id.replace(/[^a-zA-Z0-9]/g, "_");
				cell.meta.id = cell.meta.id.replace(/_+/g, "_");
				cell.meta.heatmapData = heatmapData[cell.meta.id];

				//@ts-ignore
				delete cell.meta.selecteables;

				cell.meta.selectables = selectablesMap[cell.meta.id] ?? [];
				// logDebug('cell.meta', cell.meta.id, cell.meta.selectables);
				if (cell.meta.heatmapData && cell.meta.heatmapData.length > 0) {
					cell.meta.heatMapProcessedData.count = Math.max(0, cell.meta.heatmapData.length - 3);
					const value = cell.meta.heatMapProcessedData.count / maxHeatmapValue;
					// Calculate red and green values
					const red = 0; //Math.floor(255 * (1 - value));
					const green = 255;
					const blue = 0; // Blue is not used in this gradient
					const alpha = Math.min(1, value * 2);
					cell.meta.heatMapProcessedData.bgColor = `rgba(${red}, ${green}, ${blue},${alpha})`;
					if (cell.meta.heatmapData.length > 30) {
						cell.meta.heatMapProcessedData.bgColor = `rgba(${red}, ${green}, ${blue},${1})`;
					}
					cell.meta.heatMapProcessedData.candidates = this._extractCandidates(cell.meta.heatmapData);
				} else {
					cell.meta.heatMapProcessedData.bgColor = "";
				}
				break;
			}
		}
	}

	private async _loadDataFromServer(fetchHeatmap = true, ticType?: string) {
		logDebug("Loading data from server", fetchHeatmap, ticType);
		this.loadingData = true;
		const templateDataKey = `__templateData_${ticType}_v4.1`;
		try {
			let td:
				| {
						templateData: TemplateMetaData;
						heatmap?: { [s: string]: IHeatmapData[] };
						extraction?: { [s: string]: IExtractionValue[] };
				  }
				| undefined = undefined;

			try {
				if (!this._forceFetchMetadata) {
					const cachedData = await this._privateLargeStorage.getItem(templateDataKey);
					if (cachedData) {
						td = JSON.parse(cachedData);
					}
				}
				if (!td) {
					td = await RestAPI.fetchAPI("loadTemplateData", "GET", {
						heatmap: fetchHeatmap,
						extraction: false,
						ticType: ticType || "",
					});
					logDebug("data loaded from server", td);
					// const tdhm = Object.values(td!.heatmap!).map((v) => v.filter(v1 => v1.selectedWords.length > 0)).flat();
				}
			} catch (err) {
				logError("Error loading template data", err);
			}

			if (td && td.templateData) {
				if (ticType !== "water_mitigation") {
					td.heatmap = td.heatmap || {};
					const maxHeatmapValue: number = Math.max(...Object.values(td.heatmap).map((v) => v.length));
					td.templateData.rows?.forEach((row, rowIndex) => {
						row.cellTuples.forEach((tuple, index) => {
							this._setIdToCell(tuple, { rowIndex, cellIndex: index }, td!.templateData, td!.heatmap!, maxHeatmapValue, ticType || "");
						});
					});
				}

				await this._privateLargeStorage.setItem(templateDataKey, JSON.stringify(td));
				runInAction(() => {
					this.templateData = td!.templateData;
					this.extractionData = td!.extraction;
				});
			} else {
				logError("Error loading template data");
			}
		} catch (err) {
			logError("Error loading template data", err);
		}
		this.loadingData = false;
	}

	private _loadingDataBlocking = true;
	public get loadingDataBlocking() {
		return this._loadingDataBlocking;
	}
	public set loadingDataBlocking(value: boolean) {
		this._loadingDataBlocking = value;
	}

	public get loadingData() {
		return this._loading > 0;
	}
	public set loadingData(value: boolean) {
		this._loading = value ? this._loading + 1 : this._loading - 1;
	}
}

const _mappingStore: MappingStore = new MappingStore();
const getMappingStore = () => _mappingStore;

// TODO :: move as callback to the table drawing function
export function getBackgroundClass(
	manuallyFilledData: { [s: string]: string } = {},
	perCellRenderData: { [s: string]: IPerCellRenderingData } = {},
	tupleId: string,
	leftOrRight: string
) {
	const editData = manuallyFilledData[`${tupleId}_${leftOrRight}`];
	const cellRenderData = perCellRenderData[tupleId];
	const cell = getMappingStore().getCellTupleFromId(tupleId);
	if (!cell) {
		return "";
	}
	if (editData) {
		if (cellRenderData) {
			const fullRead = (leftOrRight === "left" ? cellRenderData.leftFull : cellRenderData.rightFull).map((word) => word.toLowerCase().trim()).join(" ");
			const templateContent = (leftOrRight === "left" ? cell.cells[0] : cell.cells[1])?.content.toLowerCase().replace(/\s/g, " ").trim();
			const contentShouldBe = (templateContent + " " + editData).toLowerCase().replace(/\s/g, " ").trim();
			if (fullRead === contentShouldBe) {
				return "bg-green-200";
			}
		}
		return "bg-yellow-200";
	} else {
		if (cellRenderData) {
			const fullRead = (leftOrRight === "left" ? cellRenderData.leftFull : cellRenderData.rightFull).map((word) => word.toLowerCase().trim()).join(" ");
			const templateContent = (leftOrRight === "left" ? cell.cells[0] : cell.cells[1])?.content.toLowerCase().replace(/\s/g, " ").trim();
			const contentShouldBe = templateContent?.toLowerCase().replace(/\s/g, " ").trim();
			if (fullRead === contentShouldBe) {
				return "bg-green-200";
			} else {
				return "bg-red-200";
			}
		}
	}
	return "";
}

export interface ILinkValue {
	confidence: "confident" | "not confident";
	variable: string;
	status?: "approved" | "not approved";
}

export default class MappingUtils {
	private static _sendFakeMapping = true;
	private static _instance: MappingUtils;
	private static _localLargeStorage = new LocalLargeStorage("LassoMappingUtils");
	private static _localLargeStorageOpenAi = new LocalLargeStorage("LassoOpenAi");
	private constructor() {}
	public static instance() {
		return this._instance || (this._instance = new this());
	}

	private static OPENAI_API_KEY = import.meta.env.VITE_OPENAI_API_KEY ?? ""; // TODO :: move to server so we won't expose the key
	private static _getOpenAI() {
		const openai = new OpenAI({
			dangerouslyAllowBrowser: true,
			apiKey: MappingUtils.OPENAI_API_KEY,
		});
		return openai;
	}

	public static async openAIQueryImage(image: string, query: string) {
		const cacheKey = `${MD5(image).toString()}-${MD5(query).toString()}`;
		if (await this._localLargeStorageOpenAi.getItem(cacheKey)) {
			const testJSON = await this._localLargeStorageOpenAi.getItem(cacheKey);
			const test = JSON.parse(testJSON);
			let text: string = test.choices[0].message.content!;
			return text?.replace("```json\n", "").replace("```", "");
		}

		const base64Image = image;
		const openai = this._getOpenAI();
		try {
			const test = await openai.chat.completions.create({
				model: "gpt-4o",
				messages: [
					{
						role: "system",
						content: "You are a helpful assistant designed to output only JSON without any other text.",
					},
					{
						role: "user",
						content: [
							{
								// url: base64Image,
								type: "image_url",
								image_url: { url: `data:image/png;base64,${base64Image}` },
							},
							{
								type: "text",
								text: query,
							},
						],
					},
				],
			});
			await this._localLargeStorageOpenAi.setItem(cacheKey, JSON.stringify(test));
			let text: string = test.choices[0].message.content!;
			return text?.replace("```json\n", "").replace("```", "");
		} catch (error) {
			logError("openAIQueryImage error:", error);
		}
	}

	public static async linkVariablesUsingOpenAI(variables: string[], inputs: string[], force: boolean = false) {
		if (this._sendFakeMapping) {
			const mapping = {} as { [s: string]: ILinkValue };
			for (const input of inputs) {
				if (!mapping[input]) {
					mapping[input] = {
						variable: "not available",
						confidence: "confident",
					};
				}
			}
			return mapping;
		}

		// logDebug('linkVariablesUsingOpenAI', variables, inputs);
		variables = SharedUtils.removeDuplicateAndEmpty(variables.map((v) => v.replace(/\s/g, " ")));
		inputs = SharedUtils.removeDuplicateAndEmpty(inputs.map((v) => v.replace(/\s/g, " ")));

		const openai = this._getOpenAI();

		const content = `
		given the following glossary
		W=Wall,
		C=ceiling,
		F=floor,
		PF=Perimeter floor,
		PC=Perimeter Ceiling,
		DR=Detach&reset,
		RR=Remove&replace,
		Rem=Remove,
		R=Replace only

		and the following values ( each value in his own line )

		${variables.join("\n")}

		what value from the above will you connect to the following human input ( each input in his own line )

		${inputs.join("\n")}

		if there is something that you are not confidence about please add that you are not confident.
		each value should be mapped to only one variable from above.
		if there is no mapping available place "not available" in the variable field.
		if it maps to 2 then add that you are not confident.
		the variable value in the json must come from the list of values above.
		json file format should look like this:
		{
			"r": {
				"variable": "remove",
				"confidence": "confident|not confident"
			},
		}
		`;

		const contentHash = `${MD5(content).toString()}-v2`;
		if (!force && (await this._localLargeStorage.getItem(contentHash))) {
			return JSON.parse(await this._localLargeStorage.getItem(contentHash)!);
		}

		const test = await openai.chat.completions.create({
			model: "gpt-4o",
			messages: [
				{
					role: "system",
					content: "You are a helpful assistant designed to output only JSON without any other text.",
				},
				{
					role: "user",
					content,
				},
			],
		});

		try {
			let text: string = test.choices[0].message.content!;
			text = text.replace("```json", "").replace("```", "").trim();
			logDebug("reply from openAI", text);

			await this._localLargeStorage.setItem(contentHash, JSON.stringify(JSON.parse(text)));
			const mapping = JSON.parse(text);
			for (const input of inputs) {
				if (!mapping[input]) {
					mapping[input] = {
						variable: "not available",
						confidence: "confident",
					};
				}
			}
			return mapping;
		} catch (e) {
			logError("open AI error:", e);
		}
		return {};
	}

	getTableFromTemplate(
		_setSelectedTuple: ((tuple: [number, number]) => void) | undefined,
		_setOverTuple: ((tuple: [number, number]) => void) | undefined,
		selectedTuple: [number, number],
		OverTuple: [number, number],
		props?: CSSProperties,
		td?: TemplateMetaData,
		dbMapping?: IDatabaseMapping[],
		renderingProps: {
			editable?: boolean;
			showHeatmapData?: boolean;
			showSelectables?: boolean;
			showHasMapping?: boolean;
			plusButtonCB?: (tupleId: string) => void;
			minusButtonCB?: (tupleId: string) => void;
		} = {},
		editableData: { [s: string]: string } = {},
		perCellRenderData: { [s: string]: IPerCellRenderingData } = {},
		onEditableChange: (key: string, value: string) => void = () => {}
	) {
		const setSelectedTuple: (tuple: [number, number]) => void = _setSelectedTuple || (() => {});
		const setOverTuple: (tuple: [number, number]) => void = _setOverTuple || (() => {});
		const _templateData = _mappingStore.templateData || td;
		if (!_templateData) {
			logDebugOnce("No template data");
			return [];
		}
		const ret: (JSX.Element | JSX.Element[])[] = [];

		function hasMapping(cell: TemplateCellTuple) {
			return cell.meta?.id && dbMapping?.find((m) => m.tupleID === cell.meta?.id);
		}
		function getNotes(cell: TemplateCellTuple) {
			const mapping = dbMapping?.find((m) => m.tupleID === cell.meta?.id);
			if (mapping) {
				return mapping.notes;
			}
			return "";
		}

		_templateData!.header!.forEach((row, rowIndex) => {
			ret.push(
				<tr key={rowIndex}>
					{row.cells.map((cell, index) => {
						return (
							<td
								// colSpan={cell.columnSpan}
								key={index}
								style={
									{
										// border: `1px solid black`,
									}
								}
							>
								{cell.content}
							</td>
						);
					})}
				</tr>
			);
		});

		_templateData!.rows!.forEach((row, rowIndex) => {
			if (false) {
			} else {
				ret.push(
					<tr key={rowIndex}>
						{row.cellTuples.map((tuple, index) => {
							return (
								<td
									style={{
										padding: 0,
									}}
									key={index}
								>
									<table width="100%">
										<tbody>
											<tr
												onClick={() => {
													!_templateData.rows![rowIndex].cellTuples[index].meta?.isCategory && setSelectedTuple([rowIndex, index]);
												}}
												onMouseEnter={() =>
													!_templateData.rows![rowIndex].cellTuples[index].meta?.isCategory && setOverTuple([rowIndex, index])
												}
												onMouseLeave={() => !_templateData.rows![rowIndex].cellTuples[index].meta?.isCategory && setOverTuple([-1, -1])}
												style={{
													color: tuple.meta?.id ? "black" : "blue",
													cursor:
														_setSelectedTuple && !_templateData.rows![rowIndex].cellTuples[index].meta?.isCategory
															? "pointer"
															: "default",
													backgroundColor:
														rowIndex === OverTuple[0] && index === OverTuple[1]
															? "yellow"
															: rowIndex === selectedTuple[0] && index === selectedTuple[1]
															? "green"
															: tuple.meta?.isCategory
															? "lightblue"
															: "white",
													// border: `1px solid gray`
												}}
											>
												<td
													className="relative"
													style={{
														padding: 0,
														width: "70%",
														maxWidth: "70%",
														height: props?.height ?? 40,
														// border: `1px solid gray`
													}}
												>
													{tuple.meta?.id &&
														!_templateData.rows![rowIndex].cellTuples[index].meta?.isCategory &&
														renderingProps.plusButtonCB && (
															<div
																onClick={() => {
																	renderingProps.plusButtonCB!(tuple.meta!.id!);
																}}
																className="btn absolute right-0.5 top-0.5 bg-green-100 h-4 w-4 flex justify-center items-center"
															>
																<span>+</span>
															</div>
														)}
													{tuple.meta?.id &&
														!_templateData.rows![rowIndex].cellTuples[index].meta?.isCategory &&
														renderingProps.minusButtonCB && (
															<div
																onClick={() => {
																	renderingProps.minusButtonCB!(tuple.meta!.id!);
																}}
																className="btn absolute right-0.5 bottom-0.5 bg-sky-100 h-4 w-4 flex justify-center items-center"
															>
																<span>-</span>
															</div>
														)}
													{renderingProps.showHasMapping && (
														<span className={`mr-2 ${hasMapping(tuple) ? "bg-green-200" : ""}`}>
															({hasMapping(tuple) ? "X" : "  "})
														</span>
													)}
													<span>{tuple.cells[0].content}</span>
													{renderingProps.editable && (
														<>
															<input
																type="text"
																onChange={(e) => {
																	onEditableChange(`${tuple.meta!.id!}_left`, e.target.value);
																}}
																value={editableData[`${tuple.meta!.id!}_left`] ?? ""}
																className={`border-2  w-20 ml-2 border-gray-100 text-red-500 ${getBackgroundClass(
																	editableData,
																	perCellRenderData,
																	tuple.meta!.id!,
																	"left"
																)}`}
															/>
															<br></br>
															<span>
																{[
																	...(perCellRenderData[tuple.meta!.id!]?.leftFull || []),
																	...(perCellRenderData[tuple.meta!.id!]?.selectables || []),
																]
																	.join(" ")
																	.replace(tuple.cells[0].content, "")}
																=
															</span>
															<span>{perCellRenderData[tuple.meta!.id!]?.leftClean.join(" ")}</span>
														</>
													)}
													{renderingProps.showSelectables && (
														<>
															<br />
															<span className="text-xs">
																{tuple.meta?.selectables && JSON.stringify(tuple.meta?.selectables)}
															</span>
														</>
													)}{" "}
													{renderingProps.showHeatmapData && (
														<>
															<br />
															<div className="text-xs  flex">
																<span className="mr-1">notes:</span>
																<div className="no-break max-w-[15vw]">{getNotes(tuple)}</div>
															</div>
														</>
													)}
												</td>
												<td
													style={{
														backgroundColor: renderingProps.showHeatmapData ? tuple.meta?.heatMapProcessedData?.bgColor || "" : "",
														padding: 0,
														width: "30%",
														minWidth: "30%",
														height: props?.height ?? 40,
														// border: `1px solid gray`
													}}
												>
													<span>{tuple.cells[1]?.content || ""}</span>
													{renderingProps.editable && (
														<>
															<input
																type="text"
																onChange={(e) => {
																	onEditableChange(`${tuple.meta!.id!}_right`, e.target.value);
																}}
																value={editableData[`${tuple.meta!.id!}_right`] ?? ""}
																className={`border-2  w-20 ml-2 border-gray-100 text-red-500 ${getBackgroundClass(
																	editableData,
																	perCellRenderData,
																	tuple.meta!.id!,
																	"right"
																)}`}
															/>
															<span>
																{perCellRenderData[tuple.meta!.id!]?.rightFull.join(" ").replace(tuple.cells[1]?.content || "", "")}=
															</span>
															<span>{perCellRenderData[tuple.meta!.id!]?.rightClean.join(" ")}</span>
														</>
													)}

													<br />
													{renderingProps.showHeatmapData && !tuple.meta?.isCategory && tuple.cells[1]?.content && (
														<span>ref:{tuple.meta?.heatMapProcessedData?.count || "0"}</span>
													)}
													{/* <span>{JSON.stringify(tuple.meta?.heatMapProcessedData?.candidates, null, 1)}</span> */}
												</td>
											</tr>
										</tbody>
									</table>
								</td>
							);
						})}
					</tr>
				);
			}
		});
		return ret;
	}

	// Normalize a string (convert to lowercase and trim whitespace)
	static normalizeString(str: string): string {
		return str.toLowerCase().trim();
	}

	// Tokenize a string into words
	static tokenizeString(str: string): string[] {
		return this.normalizeString(str).split(/\s+/); // Split by any whitespace
	}

	// Check if a database string matches the search terms
	static countMatchingTokens(dbString: string, searchTerms: string[]): number {
		if (!dbString) return 0;
		const normalizedDbString = this.normalizeString(dbString);
		// return searchTerms.every(term => new RegExp(term).test(normalizedDbString));
		return searchTerms.filter((term) => {
			try {
				return new RegExp(term).test(normalizedDbString);
			} catch (error) {
				// logError('Error in regex', error);
			}
			return false;
		}).length;
	}

	// Filter database strings by input string
	static filterDatabase(db: IDatabaseLineItem[], input: string): IDatabaseLineItem[] {
		if (!input.trim() || !db || db.length === 0) return [];
		// const searchTerms = this.tokenizeString(input);
		// return db.filter(dbString => this.matchesSearchTerms(dbString.originalLineItem, searchTerms));
		const searchTerms = this.tokenizeString(input);
		const results = db
			.map((lineItem) => ({
				lineItem,
				matchingTokens: this.countMatchingTokens(lineItem.originalLineItem, searchTerms),
			}))
			.filter((result) => result.matchingTokens > 0)
			.sort((a, b) => b.matchingTokens - a.matchingTokens);

		return results.map((result) => result.lineItem);
	}
}

export { getMappingStore };
